import React, { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ContentPreview } from '@/components/ui/content-preview'
import { ContentEditor } from '@/components/ui/content-editor'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Home,
  FileText,
  Globe,
  Settings,
  Layout,
  Menu,
  Star,
  Copy,
  Archive
} from 'lucide-react'
import { supabase, StaticPageRecord } from '@/lib/supabase'
import slugify from 'slugify'

const fetchStaticPages = async (): Promise<StaticPageRecord[]> => {
  const { data, error } = await supabase
    .from('static_pages')
    .select('*')
    .order('updated_at', { ascending: false })

  if (error) throw error
  return data ?? []
}

type StaticPageDraft = Partial<StaticPageRecord> & {
  title: string
  slug: string
  content: string
  template: StaticPageRecord['template']
  status: StaticPageRecord['status']
  menu_order: number
  is_homepage: boolean
  show_in_nav: boolean
}

export default function StaticPagesManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all')
  const [templateFilter, setTemplateFilter] = useState<'all' | 'default' | 'about' | 'contact' | 'landing' | 'legal'>('all')
  const [previewPage, setPreviewPage] = useState<StaticPageRecord | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [pageDraft, setPageDraft] = useState<StaticPageDraft | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)

  const queryClient = useQueryClient()
  const { data: pages = [], isLoading, error } = useQuery({
    queryKey: ['static_pages'],
    queryFn: fetchStaticPages
  })

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.content.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || page.status === statusFilter
    const matchesTemplate = templateFilter === 'all' || page.template === templateFilter
    return matchesSearch && matchesStatus && matchesTemplate
  })

  const getStatusColor = (status: StaticPageRecord['status']) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'archived': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTemplateColor = (template: StaticPageRecord['template']) => {
    switch (template) {
      case 'landing': return 'bg-purple-100 text-purple-800'
      case 'about': return 'bg-blue-100 text-blue-800'
      case 'contact': return 'bg-green-100 text-green-800'
      case 'legal': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTemplateIcon = (template: StaticPageRecord['template']) => {
    switch (template) {
      case 'landing': return <Star className="h-3 w-3" />
      case 'about': return <FileText className="h-3 w-3" />
      case 'contact': return <Settings className="h-3 w-3" />
      case 'legal': return <Archive className="h-3 w-3" />
      default: return <Layout className="h-3 w-3" />
    }
  }

  const deleteMutation = useMutation({
    mutationFn: async (page: StaticPageRecord) => {
      const { error } = await supabase
        .from('static_pages')
        .delete()
        .eq('id', page.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['static_pages'] })
    }
  })

  const upsertMutation = useMutation({
    mutationFn: async (payload: StaticPageDraft) => {
      const baseData = {
        title: payload.title,
        slug: payload.slug,
        content: payload.content,
        excerpt: payload.excerpt ?? '',
        template: payload.template,
        status: payload.status,
        menu_order: payload.menu_order,
        is_homepage: payload.is_homepage,
        show_in_nav: payload.show_in_nav,
        featured_image: payload.featured_image ?? null,
        parent_id: payload.parent_id ?? null,
        meta_title: payload.meta_title ?? null,
        meta_description: payload.meta_description ?? null,
        updated_at: new Date().toISOString()
      }

      if (payload.id) {
        const { data, error } = await supabase
          .from('static_pages')
          .update(baseData)
          .eq('id', payload.id)
          .select('*')
          .single()

        if (error) throw error
        return data
      }

      const insertData = {
        ...baseData,
        created_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('static_pages')
        .insert([insertData])
        .select('*')
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['static_pages'] })
      setIsEditorOpen(false)
      setPageDraft(null)
    }
  })

  const handleDelete = (page: StaticPageRecord) => {
    if (page.is_homepage) {
      alert('Cannot delete the homepage. Please set another page as homepage first.')
      return
    }
    if (confirm('Are you sure you want to delete this page? This action cannot be undone.')) {
      deleteMutation.mutate(page)
    }
  }

  const handleDuplicate = (page: StaticPageRecord) => {
    openEditor({
      ...page,
      id: undefined,
      title: `${page.title} (Copy)`,
      slug: `${page.slug}-copy`,
      status: 'draft',
      menu_order: nextMenuOrder,
      is_homepage: false,
      show_in_nav: false
    })
  }

  const stats = {
    total: pages.length,
    published: pages.filter(p => p.status === 'published').length,
    draft: pages.filter(p => p.status === 'draft').length,
    homepage: pages.filter(p => p.is_homepage).length,
    navigation: pages.filter(p => p.show_in_nav).length
  }

  const templates = [
    { value: 'default', label: 'Default', count: pages.filter(p => p.template === 'default').length },
    { value: 'landing', label: 'Landing', count: pages.filter(p => p.template === 'landing').length },
    { value: 'about', label: 'About', count: pages.filter(p => p.template === 'about').length },
    { value: 'contact', label: 'Contact', count: pages.filter(p => p.template === 'contact').length },
    { value: 'legal', label: 'Legal', count: pages.filter(p => p.template === 'legal').length }
  ]

  const nextMenuOrder = useMemo(() => {
    if (!pages.length) return 1
    const maxOrder = Math.max(...pages.map(page => page.menu_order ?? 0))
    return Number.isFinite(maxOrder) ? maxOrder + 1 : 1
  }, [pages])

  const openEditor = (page?: StaticPageRecord | StaticPageDraft) => {
    if (page) {
      setPageDraft({
        id: page.id,
        title: page.title,
        slug: page.slug,
        content: page.content,
        excerpt: page.excerpt ?? '',
        template: page.template,
        status: page.status,
        menu_order: page.menu_order ?? nextMenuOrder,
        is_homepage: Boolean(page.is_homepage),
        show_in_nav: Boolean(page.show_in_nav),
        featured_image: page.featured_image,
        parent_id: page.parent_id,
        meta_title: page.meta_title,
        meta_description: page.meta_description,
      })
    } else {
      setPageDraft({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        template: 'default',
        status: 'draft',
        menu_order: nextMenuOrder,
        is_homepage: false,
        show_in_nav: true,
      })
    }
    setIsEditorOpen(true)
  }

  const handleNewPage = () => openEditor()
  const handleEditPage = (page: StaticPageRecord) => openEditor(page)
  const handlePreviewPage = (page: StaticPageRecord) => {
    setPreviewPage(page)
    setIsPreviewOpen(true)
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-1/3 rounded bg-muted animate-pulse" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="h-24 rounded bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-medium">Unable to load static pages. Please try again later.</p>
      </div>
    )
  }

  const handleEditorClose = (open: boolean) => {
    setIsEditorOpen(open)
    if (!open) {
      setPageDraft(null)
    }
  }

  const updateDraftField = <K extends keyof StaticPageDraft>(field: K, value: StaticPageDraft[K]) => {
    setPageDraft(prev => (prev ? { ...prev, [field]: value } : prev))
  }

  const handleEditorSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!pageDraft) return
    const slugValue = pageDraft.slug?.trim() || slugify(pageDraft.title, { lower: true, strict: true })
    upsertMutation.mutate({
      ...pageDraft,
      slug: slugValue
    })
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Static Pages Management</h1>
          <p className="text-muted-foreground">
            Create and manage website pages with custom templates and content.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Layout className="mr-2 h-4 w-4" />
            Page Builder
          </Button>
          <Button onClick={handleNewPage}>
            <Plus className="mr-2 h-4 w-4" />
            New Page
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Pages</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All static pages
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.published}</div>
            <p className="text-xs text-muted-foreground">
              Live on website
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Drafts</CardTitle>
            <Edit className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draft}</div>
            <p className="text-xs text-muted-foreground">
              Work in progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Homepage</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.homepage}</div>
            <p className="text-xs text-muted-foreground">
              Default landing page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Navigation</CardTitle>
            <Menu className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.navigation}</div>
            <p className="text-xs text-muted-foreground">
              Visible in menu
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle>Page Directory</CardTitle>
              <CardDescription>
                Manage website pages, templates, and navigation structure.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search pages..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="px-3 py-2 border border-input rounded-md bg-background text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                  <select
                    value={templateFilter}
                    onChange={(e) => setTemplateFilter(e.target.value as any)}
                    className="px-3 py-2 border border-input rounded-md bg-background text-sm"
                  >
                    <option value="all">All Templates</option>
                    <option value="default">Default</option>
                    <option value="landing">Landing</option>
                    <option value="about">About</option>
                    <option value="contact">Contact</option>
                    <option value="legal">Legal</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="mr-2 h-4 w-4" />
                    More Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pages Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Navigation</TableHead>
                    <TableHead>SEO Score</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPages.map((page) => (
                    <TableRow key={page.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium flex items-center gap-2">
                            {page.is_homepage && (
                              <Home className="h-4 w-4 text-blue-500" />
                            )}
                            {page.title}
                          </div>
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {page.excerpt || page.content.substring(0, 100) + '...'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            /{page.slug}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(page.status)}>
                          {page.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getTemplateColor(page.template)}>
                          <div className="flex items-center gap-1">
                            {getTemplateIcon(page.template)}
                            {page.template}
                          </div>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {page.show_in_nav ? (
                            <div className="flex items-center gap-1">
                              <Menu className="h-4 w-4 text-green-500" />
                              <span className="text-sm">Menu #{page.menu_order}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Hidden</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500" 
                              style={{ width: '88%' }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground">88%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {new Date(page.updated_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreviewPage(page)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditPage(page)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => handleDuplicate(page)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(page)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Page Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {templates.map((template) => (
                <div key={template.value} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                      {getTemplateIcon(template.value as StaticPageRecord['template'])}
                    <span className="text-sm">{template.label}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {template.count}
                  </Badge>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="w-full mt-2">
                <Plus className="mr-2 h-4 w-4" />
                Custom Template
              </Button>
            </CardContent>
          </Card>

          {/* Navigation Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Navigation Menu</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                  {pages
                    .filter(p => p.show_in_nav)
                    .sort((a, b) => (a.menu_order ?? 0) - (b.menu_order ?? 0))
                    .map((page) => (
                      <div key={page.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Menu className="h-3 w-3 text-muted-foreground" />
                        <span>{page.title}</span>
                      </div>
                        <span className="text-muted-foreground">{page.menu_order ?? '—'}</span>
                      </div>
                    ))}
              </div>
              <Button variant="outline" size="sm" className="w-full mt-3">
                <Settings className="mr-2 h-4 w-4" />
                Manage Menu
              </Button>
            </CardContent>
          </Card>

          {/* Page Hierarchy */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Page Hierarchy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="text-sm">
                  <div className="font-medium">Root Pages</div>
                  <div className="text-muted-foreground">
                    {pages.filter(p => !p.parent_id).length} pages
                  </div>
                </div>
                <div className="text-sm">
                  <div className="font-medium">Child Pages</div>
                  <div className="text-muted-foreground">
                    {pages.filter(p => p.parent_id).length} pages
                  </div>
                </div>
                <div className="text-sm">
                  <div className="font-medium">Max Depth</div>
                  <div className="text-muted-foreground">2 levels</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Desktop/tablet stacked */}
              <div className="hidden sm:block">
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Layout className="mr-2 h-4 w-4" />
                    Page Builder
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Settings className="mr-2 h-4 w-4" />
                    Site Settings
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Globe className="mr-2 h-4 w-4" />
                    Preview Site
                  </Button>
                </div>
              </div>
              {/* Mobile horizontal scroll */}
              <div className="sm:hidden">
                <ScrollArea className="w-full">
                  <div className="flex gap-2 pb-2">
                    <Button variant="outline" size="sm" className="min-w-[200px] justify-start h-auto p-3">
                      <Layout className="mr-2 h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium leading-tight">Page Builder</div>
                        <div className="text-xs text-muted-foreground">Design pages</div>
                      </div>
                    </Button>
                    <Button variant="outline" size="sm" className="min-w-[200px] justify-start h-auto p-3">
                      <Settings className="mr-2 h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium leading-tight">Site Settings</div>
                        <div className="text-xs text-muted-foreground">Configure options</div>
                      </div>
                    </Button>
                    <Button variant="outline" size="sm" className="min-w-[200px] justify-start h-auto p-3">
                      <Globe className="mr-2 h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm font-medium leading-tight">Preview Site</div>
                        <div className="text-xs text-muted-foreground">Open in new tab</div>
                      </div>
                    </Button>
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
      </div>
    </div>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewPage?.title || 'Static Page Preview'}</DialogTitle>
          </DialogHeader>
          {previewPage && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>/{previewPage.slug}</span>
                <span>•</span>
                <span className="capitalize">{previewPage.status}</span>
              </div>
              <ContentPreview
                content={previewPage.content}
                maxHeight="none"
                showBorder={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isEditorOpen} onOpenChange={handleEditorClose}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{pageDraft?.id ? 'Edit Page' : 'Create Page'}</DialogTitle>
            <CardDescription>Update metadata, template, and content for this static page.</CardDescription>
          </DialogHeader>
          {pageDraft && (
            <form className="space-y-4" onSubmit={handleEditorSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="page-title">Title</Label>
                  <Input
                    id="page-title"
                    value={pageDraft.title}
                    onChange={(e) => updateDraftField('title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="page-slug">Slug</Label>
                  <Input
                    id="page-slug"
                    value={pageDraft.slug}
                    onChange={(e) => updateDraftField('slug', e.target.value)}
                    placeholder="about-us"
                  />
                  <p className="text-xs text-muted-foreground mt-1">URL: /{pageDraft.slug || slugify(pageDraft.title || 'page', { lower: true, strict: true })}</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label>Template</Label>
                  <Select value={pageDraft.template} onValueChange={(value) => updateDraftField('template', value as StaticPageDraft['template'])}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="landing">Landing</SelectItem>
                      <SelectItem value="about">About</SelectItem>
                      <SelectItem value="contact">Contact</SelectItem>
                      <SelectItem value="legal">Legal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={pageDraft.status} onValueChange={(value) => updateDraftField('status', value as StaticPageDraft['status'])}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="menu-order">Menu Order</Label>
                  <Input
                    id="menu-order"
                    type="number"
                    min={0}
                    value={pageDraft.menu_order}
                    onChange={(e) => updateDraftField('menu_order', Number(e.target.value) || 0)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="font-medium">Show in Navigation</p>
                    <p className="text-sm text-muted-foreground">Include in the primary menu.</p>
                  </div>
                  <Switch checked={pageDraft.show_in_nav} onCheckedChange={(checked) => updateDraftField('show_in_nav', Boolean(checked))} />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="font-medium">Homepage</p>
                    <p className="text-sm text-muted-foreground">Mark as the default landing page.</p>
                  </div>
                  <Switch checked={pageDraft.is_homepage} onCheckedChange={(checked) => updateDraftField('is_homepage', Boolean(checked))} />
                </div>
              </div>

              <div>
                <Label htmlFor="page-excerpt">Excerpt</Label>
                <Textarea
                  id="page-excerpt"
                  value={pageDraft.excerpt}
                  onChange={(e) => updateDraftField('excerpt', e.target.value)}
                  rows={3}
                />
              </div>

              <ContentEditor
                label="Page Content"
                value={pageDraft.content}
                onChange={(content) => updateDraftField('content', content)}
                height={350}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => handleEditorClose(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsertMutation.isPending}>
                  {upsertMutation.isPending ? 'Saving…' : 'Save Page'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
